import type {
  PlatformProfileUserRole,
  PlatformUser,
  PlatformUserRole,
} from '@nucleus-commerce/api-client/platform';
import { ApiError } from '@nucleus-commerce/api-client/transport';

import { isOwner } from '@/shared/session/roles';

export const ROLE_ORDER: readonly PlatformUserRole[] = ['owner', 'admin', 'support', 'read_only'];

/** Roles the actor may hand out. Only owners create or grant owner. */
export function assignableRoles(actorRole: PlatformProfileUserRole): PlatformUserRole[] {
  return ROLE_ORDER.filter((role) => role !== 'owner' || isOwner(actorRole));
}

export interface Actor {
  id: string;
  role: PlatformProfileUserRole;
}

/** What the client can already tell about editing `target`; the backend enforces the same rules. */
export interface EditRestrictions {
  /** The actor may open the edit dialog at all (non-owners cannot touch owners). */
  canEdit: boolean;
  /** Editing yourself: role and status stay as they are. */
  isSelf: boolean;
  /** The only active owner: cannot be demoted or disabled. */
  isLastOwner: boolean;
  roleLocked: boolean;
  statusLocked: boolean;
  /** Reason key under `users:locks` for the locked controls, if any. */
  lockReason: 'self' | 'lastOwner' | null;
}

export function editRestrictions(
  actor: Actor,
  target: PlatformUser,
  users: readonly PlatformUser[],
): EditRestrictions {
  const isSelf = actor.id === target.id;
  const activeOwners = users.filter((u) => u.role === 'owner' && u.status === 'active');
  const isLastOwner =
    target.role === 'owner' &&
    target.status === 'active' &&
    activeOwners.length === 1 &&
    activeOwners[0]?.id === target.id;
  const canEdit = target.role !== 'owner' || isOwner(actor.role);
  const locked = isSelf || isLastOwner;
  return {
    canEdit,
    isSelf,
    isLastOwner,
    roleLocked: locked,
    statusLocked: locked,
    lockReason: isSelf ? 'self' : isLastOwner ? 'lastOwner' : null,
  };
}

export function canToggleStatus(
  actor: Actor,
  target: PlatformUser,
  users: readonly PlatformUser[],
): boolean {
  const r = editRestrictions(actor, target, users);
  return r.canEdit && !r.statusLocked;
}

const EDIT_CODES: Record<string, string> = {
  CANNOT_CHANGE_SELF: 'users:errors.selfChange',
  LAST_OWNER: 'users:errors.lastOwner',
  PERMISSION_DENIED: 'users:errors.ownerOnly',
};

/**
 * The backend's answers to an edit the client could not rule out (a stale list, a race). Codes
 * are the contract; the status/kind fallback covers an older backend.
 */
export function editErrorKey(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const byCode = EDIT_CODES[error.code];
  if (byCode) return byCode;
  if (error.status === 422) return 'users:errors.selfChange';
  if (error.kind === 'forbidden') return 'users:errors.ownerOnly';
  if (error.kind === 'conflict') return 'users:errors.lastOwner';
  return null;
}

/** 409 PASSWORD_ALREADY_SET: the person no longer needs the link. */
export function resendErrorKey(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.code === 'PASSWORD_ALREADY_SET' || error.kind === 'conflict') {
    return 'users:errors.alreadyHasPassword';
  }
  return null;
}
